// frontend/src/modules/projects/pages/MyTasksPage.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { DataTable } from '../../../design-system/components/DataTable';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useApi } from '../../../design-system/useApi';
import { formatDate } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot, type StatusTone } from '../../../design-system/visual';
import { ListTodo, Plus, Play, Square } from 'lucide-react';
import { projectsApi } from '../api';
import type { Task, TaskStatus, TimeEntry } from '../types';

// Everyday click-to-advance cycle, same convention as the manager task
// board on ProjectDetailPage: open → in progress → done → back to open.
const NEXT_STATUS: Record<TaskStatus, TaskStatus> = {
  open: 'in_progress',
  in_progress: 'done',
  done: 'open',
  cancelled: 'open',
};

function taskStatusPill(s: TaskStatus): React.ReactNode {
  const tone: StatusTone =
    s === 'done'        ? 'success' :
    s === 'in_progress' ? 'info' :
    s === 'cancelled'   ? 'destructive' :
    'muted';
  return (
    <span className={statusPill(tone)}>
      <span className={statusDot(tone)} />
      {s.replace('_', ' ')}
    </span>
  );
}

export function MyTasksPage() {
  const tasksQ = useApi<{ tasks: Task[] }>('/api/projects/tasks/me');
  const openQ = useApi<{ entry: TimeEntry | null }>('/api/projects/time-entries/me/open');

  const [title, setTitle] = React.useState('');
  const [dueDate, setDueDate] = React.useState('');
  const [isPrivate, setIsPrivate] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [timerBusyId, setTimerBusyId] = React.useState<string | null>(null);

  const tasks = tasksQ.data?.tasks ?? [];
  const running = openQ.data?.entry ?? null;

  async function addTask(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) { toast.error('Title is required'); return; }
    setAdding(true);
    try {
      await projectsApi.createMyTask({
        title: title.trim(),
        due_date: dueDate || null,
        is_private: isPrivate,
      });
      toast.success('Task added');
      setTitle(''); setDueDate(''); setIsPrivate(false);
      tasksQ.reload();
    } catch (err: any) {
      toast.error('Could not add task', { description: err.message });
    } finally { setAdding(false); }
  }

  async function cycleStatus(task: Task) {
    try {
      await projectsApi.updateMyTask(task.id, { status: NEXT_STATUS[task.status] });
      tasksQ.reload();
    } catch (err: any) { toast.error('Update failed', { description: err.message }); }
  }

  async function startTimer(task: Task) {
    if (!task.project_id) {
      toast.error('Cannot time this task', { description: 'It has no project attached — ask a manager to assign one.' });
      return;
    }
    setTimerBusyId(task.id);
    try {
      await projectsApi.startTimer({ project_id: task.project_id, task_id: task.id });
      toast.success('Timer started');
      openQ.reload();
    } catch (err: any) {
      toast.error('Could not start timer', { description: err.message });
    } finally { setTimerBusyId(null); }
  }

  async function stopTimer() {
    setTimerBusyId(running?.task_id ?? 'stop');
    try {
      await projectsApi.stopTimer();
      toast.success('Timer stopped');
      openQ.reload();
    } catch (err: any) {
      toast.error('Could not stop timer', { description: err.message });
    } finally { setTimerBusyId(null); }
  }

  if (tasksQ.error) {
    return (
      <div className="px-6 sm:px-8 lg:px-10 py-12 max-w-4xl">
        {tasksQ.status === 403
          ? <NoAccessState description="You don't have access to your tasks." />
          : <ErrorState title="Couldn't load your tasks" description={tasksQ.error} onRetry={tasksQ.reload} />}
      </div>
    );
  }
  if (tasksQ.loading) return <LoadingState variant="page" />;

  return (
    <div>
      <PageHeader
        eyebrow="Projects"
        title="My Tasks"
        description="Tasks assigned to you, plus any personal tasks you've added yourself."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-4xl space-y-6">
        <section className={[VISUAL.bento, 'p-5'].join(' ')}>
          <form onSubmit={addTask} className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>New task</Label>
              <Input
                placeholder="What do you need to do?"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <div className="grid gap-1.5">
                <Label>Due date (optional)</Label>
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
              <label className="flex items-center gap-2.5 text-[13px] text-foreground cursor-pointer select-none h-10">
                <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)}
                  className="h-4 w-4 rounded border-border accent-saffron" />
                Private
              </label>
              <Button type="submit" disabled={adding} className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5 ml-auto">
                <Plus className="h-3.5 w-3.5" />
                {adding ? 'Adding…' : 'Add task'}
              </Button>
            </div>
          </form>
        </section>

        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h3 className={VISUAL.dashSection}>Tasks</h3>
            <p className="text-[12px] text-muted-foreground">{tasks.length} total</p>
          </div>
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <DataTable<Task>
              rows={tasks}
              rowKey={(r) => r.id}
              columns={[
                { key: 'title', header: 'Title', cell: (r) => (
                  <div>
                    <span className="font-medium text-foreground">{r.title}</span>
                    {r.is_private && <span className="ml-2 text-[11px] text-muted-foreground">(private)</span>}
                  </div>
                ) },
                { key: 'status', header: 'Status', cell: (r) => (
                  <button
                    type="button"
                    onClick={() => cycleStatus(r)}
                    className="cursor-pointer"
                    title="Click to advance: open → in progress → done"
                  >
                    {taskStatusPill(r.status)}
                  </button>
                ) },
                { key: 'due', header: 'Due', cell: (r) => r.due_date ? formatDate(r.due_date) : '—', className: 'text-muted-foreground' },
                { key: 'timer', header: '', cell: (r) => {
                  const isRunningHere = running?.task_id === r.id;
                  if (isRunningHere) {
                    return (
                      <div className="flex justify-end">
                        <Button size="sm" variant="outline" disabled={timerBusyId !== null} onClick={stopTimer}
                          className="rounded-full h-8 px-3 text-[12px] font-semibold gap-1.5">
                          <Square className="h-3 w-3" />
                          Stop
                        </Button>
                      </div>
                    );
                  }
                  return (
                    <div className="flex justify-end">
                      <Button size="sm" variant="ghost" disabled={!!running || timerBusyId !== null || !r.project_id}
                        title={!r.project_id ? 'No project attached' : running ? 'Stop the running timer first' : 'Start timer'}
                        onClick={() => startTimer(r)}
                        className="rounded-full h-8 px-3 text-[12px] font-semibold gap-1.5">
                        <Play className="h-3 w-3" />
                        Start
                      </Button>
                    </div>
                  );
                } },
              ]}
              emptyState={
                <EmptyState icon={<ListTodo />} title="No tasks yet" description="Add your first task above." />
              }
            />
          </div>
        </section>
      </div>
    </div>
  );
}
